/**
 * `AccountUsageStrip` — account-wide 30-day totals on the All-sites
 * page. Wired 2026-07-06 (founder report: the ported placeholder
 * rendered three em-dashes and explained nothing).
 *
 * Data: one `reporting.getOverview(site, '30d')` per listed site,
 * summed client-side. Only EXACT tiles render — agent calls, settled
 * revenue, pending disputes — all directly from the overview KPIs.
 * (The prototype's "distinct agents" and "audit records" tiles need
 * account-level endpoints that don't exist; they were dropped rather
 * than approximated.)
 *
 * KPI semantics (mirror the per-site Dashboard tiles): `agent_calls`
 * = audit records with a matched agent identity in the window;
 * `revenue_minor` = GROSS succeeded settlements (refund rows excluded,
 * not deducted); `disputes_pending` (wire name is historical) counts
 * settlement outcomes IN ('failed','disputed') in the window — label
 * it "Failed / disputed", NEVER "pending" (founder field report
 * 2026-07-06: rejected/expired test settlements read as phantom
 * pending items).
 *
 * Honesty rules:
 *   - Any per-site fetch failure → all tiles show "—" with a quiet
 *     "couldn't load" note (a silent partial sum would under-count).
 *   - Revenue sums only when every reporting site shares one currency;
 *     mixed currencies render "—" with a "mixed currencies" note.
 *   - Zero sites → the strip renders nothing.
 */

import { useEffect, useState } from 'react';

import type { ReportingOverview, SiteId } from '@sill/types';

import { useOptionalApi } from '../../api/api-context.js';
import { useSites } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";

const CURRENCY_SYMBOLS: Readonly<Record<string, string>> = {
  USD: '$',
  CAD: '$',
  AUD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
};

function formatMinor(minor: number, currency: string): string {
  const sym = CURRENCY_SYMBOLS[currency.toUpperCase()] ?? `${currency.toUpperCase()} `;
  if (currency.toUpperCase() === 'JPY') return `${sym}${Math.round(minor).toLocaleString()}`;
  return `${sym}${(minor / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

type StripState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | {
      kind: 'ready';
      agent_calls: number;
      revenue: { total_minor: number; currency: string } | 'none' | 'mixed';
      disputes_pending: number;
    };

export function AccountUsageStrip(): JSX.Element | null {
  const C = useC();
  const api = useOptionalApi();
  const { sites } = useSites();
  const [state, setState] = useState<StripState>({ kind: 'loading' });

  const site_ids = sites.map((s) => s.site_id).join(',');

  useEffect(() => {
    if (api === null || sites.length === 0) return;
    let cancelled = false;
    void Promise.all(
      sites.map((s) => api.reporting.getOverview(s.site_id as SiteId, { range: '30d' })),
    )
      .then((overviews: ReportingOverview[]) => {
        if (cancelled) return;
        const agent_calls = overviews.reduce((n, o) => n + o.agent_calls.count, 0);
        const disputes_pending = overviews.reduce((n, o) => n + o.disputes_pending.count, 0);
        const currencies = new Set(
          overviews.map((o) => o.revenue_minor.currency).filter((c): c is string => c !== null),
        );
        const revenue =
          currencies.size === 0
            ? ('none' as const)
            : currencies.size > 1
              ? ('mixed' as const)
              : {
                  total_minor: overviews.reduce((n, o) => n + o.revenue_minor.total_minor, 0),
                  currency: [...currencies][0]!,
                };
        setState({ kind: 'ready', agent_calls, revenue, disputes_pending });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, site_ids]);

  if (sites.length === 0) return null;

  const value = (v: string): JSX.Element => (
    <div style={{ fontFamily: serifFont, fontSize: 32, color: C.text, letterSpacing: -1, lineHeight: 1 }}>{v}</div>
  );
  const kicker = (t: string): JSX.Element => (
    <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 6 }}>{t}</div>
  );
  const sub = (t: string): JSX.Element => (
    <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 4 }}>{t}</div>
  );

  const calls =
    state.kind === 'ready' ? state.agent_calls.toLocaleString() : state.kind === 'loading' ? '…' : '—';
  const disputes =
    state.kind === 'ready' ? state.disputes_pending.toLocaleString() : state.kind === 'loading' ? '…' : '—';
  const revenueValue =
    state.kind === 'ready'
      ? state.revenue === 'none'
        ? '—'
        : state.revenue === 'mixed'
          ? '—'
          : formatMinor(state.revenue.total_minor, state.revenue.currency)
      : state.kind === 'loading'
        ? '…'
        : '—';
  const revenueSub =
    state.kind === 'ready' && state.revenue === 'mixed'
      ? 'mixed currencies — see per-site reporting'
      : state.kind === 'ready' && state.revenue === 'none'
        ? 'no settled revenue in the window'
        : 'settled via your rails (before refunds)';
  const failNote = state.kind === 'error' ? " — couldn't load" : '';

  return (
    <div
      className="mt-10 p-6 grid grid-cols-1 md:grid-cols-3 gap-6"
      style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 4 }}
    >
      <div>
        {kicker('AGENT CALLS · ALL SITES · 30D')}
        {value(calls)}
        {sub(`observed across ${sites.length} ${sites.length === 1 ? 'site' : 'sites'}${failNote}`)}
      </div>
      <div>
        {kicker('REVENUE · 30D')}
        {value(revenueValue)}
        {sub(`${revenueSub}${failNote}`)}
      </div>
      <div>
        {kicker('FAILED / DISPUTED · 30D')}
        {value(disputes)}
        {sub(`settlement failures + disputes, all sites${failNote}`)}
      </div>
    </div>
  );
}
